import {
  signIn,
  confirmSignIn,
  signOut,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  type SignInOutput,
} from 'aws-amplify/auth'
import { isCognitoConfigured } from '../config/cognito'
import { isNativeApp } from '../utils/platform'
import { nativeAuthSessionOpener } from '../utils/native-oauth'
import { fetchMe, isAppSyncConfigured, resetCoalescedReads } from './appsync-client'
import { normalizeTotpCode } from '../features/security/totp-code'
import { resetDirectoryStore } from '../stores/directory-store'
import { clearAllLocalCache } from './local-cache'
import {
  hydrateRecordingPolicyConsentFromProfile,
  resetRecordingPolicyConsent,
} from '../stores/recording-policy-store'
import { resetConsentCallEndedNotice } from '../stores/consent-call-ended-store'
import { resetSetupProfile } from '../stores/setup-profile-store'

export type AuthUser = {
  userId: string
  email: string
  displayName: string
  givenName?: string | null
  familyName?: string | null
  roleType?: string | null
  providerLevel?: number | null
  avatarUrl?: string | null
  title?: string | null
  settingsJson?: Record<string, unknown> | null
  /** Account lifecycle. PENDING_INVITE = invited, awaiting owner approval. */
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING_INVITE' | null
  /** Independent removed from all Member associations — show login notice. */
  associationRemovalNotice?: boolean | null
}

function parseSettingsJson(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return null
    }
  }
  return raw as Record<string, unknown>
}

function assertConfigured() {
  if (!isCognitoConfigured()) {
    throw new Error('Cognito is not configured. Set VITE_COGNITO_* in frontend/.env.local')
  }
}

function isAlreadySignedInError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const name = 'name' in err ? String((err as { name?: string }).name) : ''
  const message = 'message' in err ? String((err as { message?: string }).message) : String(err)
  return (
    name === 'UserAlreadyAuthenticatedException' ||
    /already a signed in user/i.test(message)
  )
}

/** True when Cognito confirms there is no restorable session (not a network blip). */
function isDefinitiveAuthFailure(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const name = 'name' in err ? String((err as { name?: string }).name) : ''
  const message = 'message' in err ? String((err as { message?: string }).message) : String(err)
  return (
    name === 'NotAuthorizedException' ||
    name === 'UserNotFoundException' ||
    name === 'UserUnAuthenticatedException' ||
    name === 'UserNotConfirmedException' ||
    /refresh token has expired/i.test(message) ||
    /invalid refresh token/i.test(message) ||
    /user is not authenticated/i.test(message) ||
    /no current user/i.test(message) ||
    /token expired/i.test(message) ||
    // Amplify AppSync client error when fetchAuthSession has no access token.
    /no federated jwt/i.test(message) ||
    name === 'NoValidAuthTokens'
  )
}

function isTransientError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return true
  const name = 'name' in err ? String((err as { name?: string }).name) : ''
  const message = 'message' in err ? String((err as { message?: string }).message) : String(err)
  return (
    name === 'NetworkError' ||
    /network/i.test(message) ||
    /fetch failed/i.test(message) ||
    /timeout/i.test(message) ||
    /failed to fetch/i.test(message)
  )
}

/**
 * Clear Amplify tokens on THIS device only (fixes stale browser sessions on
 * the sign-in page). Deliberately NOT a global sign-out: `global: true`
 * revokes the user's refresh tokens on every device, which silently logged
 * the native app out whenever the same account signed in on the web.
 */
export async function clearAuthSession(): Promise<void> {
  try {
    await signOut()
  } catch {
    /* no active session */
  }
  // A forced local clear (expired / stale session, new sign-in starting) keeps
  // the account's un-uploaded session audio so it can finish after signing back
  // in; another account signing in on this device deletes it
  // (`claimSessionRecordingAudio`). Hidden-test recordings always go.
  await wipeRecorderTestDataBestEffort()
}

/** Delete the hidden-test recordings; never throws (no-op on web). */
export async function wipeRecorderTestDataBestEffort(): Promise<void> {
  try {
    const recorder = await import('./session-recorder')
    await recorder.wipeTestRecordings()
  } catch {
    /* best-effort — the native 24 h test sweep is the backstop */
  }
}

/** Hosted UI (authorization code + PKCE handled by Amplify) */
export async function signInWithHostedUi(): Promise<void> {
  assertConfigured()
  await clearAuthSession()
  await signInWithRedirect(
    isNativeApp()
      ? { options: { authSessionOpener: nativeAuthSessionOpener } }
      : undefined,
  )
}

export async function requestPasswordReset(email: string): Promise<void> {
  assertConfigured()
  await resetPassword({ username: email.trim() })
}

export async function completePasswordReset(
  email: string,
  confirmationCode: string,
  newPassword: string,
): Promise<void> {
  assertConfigured()
  await confirmResetPassword({
    username: email.trim(),
    confirmationCode: confirmationCode.trim(),
    newPassword,
  })
}

export async function signInWithEmail(email: string, password: string): Promise<SignInOutput> {
  assertConfigured()
  try {
    return await signIn({ username: email, password })
  } catch (err) {
    if (!isAlreadySignedInError(err)) throw err
    await clearAuthSession()
    return signIn({ username: email, password })
  }
}

/**
 * Complete the Cognito `CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED` challenge.
 * Invited users created via `admin-create-user` start in `FORCE_CHANGE_PASSWORD`
 * and must set a permanent password right after entering their temporary one.
 */
export async function confirmNewPasswordChallenge(newPassword: string): Promise<SignInOutput> {
  assertConfigured()
  return confirmSignIn({ challengeResponse: newPassword })
}

/**
 * Complete the Cognito `CONFIRM_SIGN_IN_WITH_TOTP_CODE` challenge (and the
 * `CONTINUE_SIGN_IN_WITH_TOTP_SETUP` variant, which takes the same response).
 * Only accounts with a verified authenticator ever reach this — the pool is
 * MFA=OPTIONAL, so everyone else signs in on the password step as before.
 */
export async function confirmTotpChallenge(code: string): Promise<SignInOutput> {
  assertConfigured()
  return confirmSignIn({ challengeResponse: normalizeTotpCode(code) })
}

export async function signOutUser(): Promise<void> {
  // Fail-closed: unlike clearAuthSession() (sign-in page hygiene, where a
  // failed teardown is harmless), an explicit sign-out must surface failure —
  // callers keep soft-lock clocks and session state armed until the local
  // tokens are actually cleared. Amplify's non-global signOut clears local
  // tokens even when the network revoke fails, so an offline sign-out still
  // resolves; only a genuine local teardown failure (e.g. token storage)
  // rejects here.
  //
  // Push rows are deleted first because the mutation needs the session; a
  // shared device must stop ringing for this account the moment it signs out.
  let signedOutUserId: string | null = null
  try {
    const { userId } = await getCurrentUser()
    signedOutUserId = userId
    const push = await import('./push-registration')
    await push.unregisterPushForSignOut({ userId })
  } catch {
    /* best-effort — the next account's registration also releases the token */
  }
  try {
    await signOut()
  } catch (err) {
    // Session already gone on this device — teardown is effectively complete.
    if (!isDefinitiveAuthFailure(err)) throw err
  }
  // Module-level caches outlive the session (no page reload on sign-out): drop
  // the previous user's contact roster so the next account on this device
  // never sees it. directory-store is imported statically — the always-mounted
  // call overlays already pull it (and chat-service) into the entry chunk, and
  // a mixed static/dynamic import defeats Vite's code splitting. chat-service
  // stays dynamic so auth-service keeps no direct edge into the chat graph.
  //
  // Every step is INDEPENDENT (own try): one failing cleanup used to skip
  // every step after it in the same block, leaving the previous account's
  // native call state, car snapshots or caches on a shared device.
  //
  // The cached "me" feeds call signalling synchronously: left in place, the
  // next account on this device placed calls as (and rejected calls meant for)
  // the previous one. Reset it first.
  // FIRST (synchronously, right after `signOut()` resolved): drop the coalesced
  // `me` / `listConversations` reads. They are shared per-process for a few
  // seconds, so left in place a `getCurrentChatUser()` fired during the rest of
  // this teardown (calls path) would be handed the PREVIOUS user's profile.
  // Inline (no await before it) so not even a microtask can slip in between.
  try {
    resetCoalescedReads()
  } catch (err) {
    console.warn('[auth] sign-out cleanup step failed: coalescedReads', err)
  }
  await runSignOutStep('chatUser', async () => {
    const chat = await import('./chat-service')
    chat.resetCachedChatUser()
  })
  // On-device screen snapshots (Sessions / inbox / calls / notifications) hold
  // the previous user's member names: wipe them so the next account on a shared
  // phone never paints them. Own try, like every step.
  await runSignOutStep('localCache', () => clearAllLocalCache())
  // Native call state of the signed-out account (Android + iOS): native-ack
  // credentials, invite seed / pending invites, pending actions, rings,
  // Telecom / CallKit calls, JoinInfo caches, the ongoing-call service, car
  // avatar/recents caches, the location buffer.
  await runSignOutStep('nativeCallState', async () => {
    const native = await import('./voice-call/native-call')
    await native.clearNativeUserState('signOut')
  })
  await runSignOutStep('directory', () => resetDirectoryStore())
  await runSignOutStep('recordingPolicy', () => resetRecordingPolicyConsent())
  await runSignOutStep('setupProfile', () => resetSetupProfile())
  await runSignOutStep('consentNotice', () => resetConsentCallEndedNotice())
  await runSignOutStep('directoryCache', async () => {
    const chat = await import('./chat-service')
    chat.invalidateDirectoryUsersCache()
  })
  await runSignOutStep('carContacts', async () => {
    const car = await import('../features/calls/utils/car-contacts-sync')
    await car.clearCarContactsSnapshot()
  })
  // Member names in the Android Auto sessions snapshot are the previous
  // user's clinical data — wipe them the same way as the contact roster.
  await runSignOutStep('carSessions', async () => {
    const carSessions = await import('../features/sessions/utils/car-sessions-sync')
    await carSessions.clearCarSessionsSnapshot()
  })
  // Message previews + sender names in the car Messages tab likewise.
  await runSignOutStep('carMessages', async () => {
    const carMessages = await import('../features/messages/utils/car-messages-sync')
    await carMessages.clearCarMessagesSnapshot()
  })
  // Unsent composer drafts are message content: purge them (and cancel the
  // debounced write so a keystroke from the closing session cannot re-persist
  // one after this teardown). Same shared-device rationale as the roster.
  await runSignOutStep('drafts', async () => {
    const drafts = await import('../utils/conversation-drafts')
    drafts.clearAllConversationDrafts()
  })
  await runSignOutStep('senderCache', async () => {
    const senderCache = await import('../utils/last-message-sender-cache')
    senderCache.resetLastMessageSenderCache()
  })
  // Remaining per-account module state: call history, the in-app message
  // toast, a parked call-waiting invite, live-location shares (they carry the
  // previous user's id) and the telemetry id token cached for unload flushes.
  await runSignOutStep('callHistory', async () => {
    const history = await import('./call-history-service')
    history.invalidateCallHistoryCache()
  })
  // Presence the previous account was allowed to see (and who it couldn't).
  await runSignOutStep('presence', async () => {
    const presence = await import('../stores/presence-store')
    presence.resetPresenceStore()
  })
  await runSignOutStep('messageToast', async () => {
    const toast = await import('../stores/message-notification-store')
    toast.clearMessageNotification()
  })
  await runSignOutStep('waitingCall', async () => {
    const waiting = await import('../stores/waiting-call-store')
    waiting.clearWaitingCall()
  })
  await runSignOutStep('liveLocation', async () => {
    const liveLocation = await import('./live-location-share-service')
    liveLocation.cancelAllLiveLocationSharesLocally()
  })
  // Pending session-call ends / the live session call are this account's
  // session records: never let the next account on this device send them.
  await runSignOutStep('sessionCallRecorder', async () => {
    const recorder = await import('../features/sessions/utils/session-call-recorder')
    // Unknown user (getCurrentUser failed): clear every account's keys rather
    // than leave this one's for the next account.
    if (signedOutUserId) recorder.clearSessionCallRecorderStorage(signedOutUserId)
    else recorder.clearAllSessionCallRecorderStorage()
  })
  await runSignOutStep('callTelemetryToken', async () => {
    const telemetry = await import('./voice-call/call-telemetry-service')
    telemetry.resetCallTelemetryToken()
  })
  // Session recorder (hidden test + Premium Session Recording): stop any running
  // recording, delete every local chunk / joined file — uploaded or not — and the
  // upload queue, so the next account on this device never inherits room audio.
  await runSignOutStep('sessionRecorderData', async () => {
    const recorder = await import('./session-recorder')
    await recorder.wipeSessionRecorderDeviceData()
  })
  // Background session tracking belongs to the signed-out account: stop the
  // location service and drop native live-ping credentials (the native fix
  // buffer itself is cleared by clearNativeUserState above).
  await runSignOutStep('backgroundLocation', async () => {
    const location = await import('./background-location')
    const credentialCache = await import('../features/sessions/data/native-live-ping-credential-cache')
    // Disarm is dispatched first; the in-memory token cache is cleared in the
    // same tick (bumping its epoch so an in-flight token fetch can neither
    // refill it nor arm native for the next user) — before awaiting either.
    let disarmed: Promise<void> | undefined
    try {
      disarmed = location.disarmNativeLivePing()
    } finally {
      credentialCache.clearNativeLivePingCredentialCache()
    }
    await disarmed
    await location.stopBackgroundLocation()
  })
}

/** One sign-out cleanup step: failures are logged and never skip later steps. */
async function runSignOutStep(name: string, step: () => unknown): Promise<void> {
  try {
    await step()
  } catch (err) {
    console.warn(`[auth] sign-out cleanup step failed: ${name}`, err)
  }
}

/**
 * Load the signed-in user. Returns null only when Cognito has no valid session.
 * Throws on transient failures (network) so callers can preserve the existing session.
 */
export async function getAuthUser(): Promise<AuthUser | null> {
  let session
  try {
    session = await fetchAuthSession()
  } catch (err) {
    if (isDefinitiveAuthFailure(err)) return null
    throw err
  }

  if (!session.tokens?.accessToken) return null

  let cognitoUser
  try {
    cognitoUser = await getCurrentUser()
  } catch (err) {
    if (isDefinitiveAuthFailure(err)) return null
    throw err
  }

  // Boot waterfall: Cognito `GetUser` (attributes) and the AppSync `me` profile
  // are independent network calls — start the profile read first and let it
  // overlap the attribute fetch instead of queueing behind it (saves one full
  // round trip on every cold start / sign-in). Outcomes are unchanged: a
  // definitive attribute failure still signs out; a profile failure is still
  // absorbed here (settled, so an early return never leaves an unhandled
  // rejection behind).
  const profileRead = isAppSyncConfigured()
    ? fetchMe().then(
        (profile) => ({ ok: true as const, profile }),
        (err: unknown) => ({ ok: false as const, err }),
      )
    : null

  let attrs: Awaited<ReturnType<typeof fetchUserAttributes>> = {}
  try {
    attrs = await fetchUserAttributes()
  } catch (err) {
    if (isDefinitiveAuthFailure(err)) return null
    // Attribute fetch can fail offline — still treat as signed in with token claims only.
    if (!isTransientError(err)) {
      console.warn('[auth] fetchUserAttributes failed; using minimal profile', err)
    }
  }

  const email = attrs.email ?? ''
  const given = attrs.given_name ?? ''
  const family = attrs.family_name ?? ''
  const displayName = [given, family].filter(Boolean).join(' ') || email.split('@')[0] || 'User'

  const base: AuthUser = {
    userId: cognitoUser.userId,
    email,
    displayName,
    givenName: attrs.given_name ?? null,
    familyName: attrs.family_name ?? null,
  }

  if (profileRead) {
    const result = await profileRead
    if (result.ok) {
      try {
        const { profile } = result
        // Keep the pre-dial recording-policy gate in sync with the profile on
        // every session load/refresh (it reads the store synchronously).
        hydrateRecordingPolicyConsentFromProfile(profile)
        return {
          ...base,
          userId: profile.userId,
          email: profile.email || email,
          displayName: profile.displayName || displayName,
          givenName: profile.givenName,
          familyName: profile.familyName,
          roleType: profile.roleType,
          providerLevel: profile.providerLevel,
          avatarUrl: profile.avatarUrl,
          title: profile.title,
          settingsJson: parseSettingsJson(profile.settingsJson),
          status: profile.status ?? null,
          associationRemovalNotice: profile.associationRemovalNotice ?? null,
        }
      } catch (err) {
        if (isDefinitiveAuthFailure(err)) return null
        return base
      }
    }
    if (isDefinitiveAuthFailure(result.err)) return null
    // AppSync unreachable — Cognito session is valid but RBAC/profile are unavailable.
    return base
  }

  return base
}

/** Proactively refresh Cognito tokens when the app returns to foreground. */
export async function keepAliveAuthSession(): Promise<void> {
  await fetchAuthSession({ forceRefresh: true })
}

export async function getAccessToken(): Promise<string | null> {
  const session = await fetchAuthSession()
  return session.tokens?.accessToken?.toString() ?? null
}
